(function () {
  'use strict';

  var SECTION_SELECTOR = '.oddit-section-featured-reviews';
  var DOT_CLASS = 'oddit-section-featured-reviews__dot';
  var DOT_ACTIVE_CLASS = 'oddit-section-featured-reviews__dot--active';
  var GRABBING_CLASS = 'oddit-section-featured-reviews__track--grabbing';

  // Desktop click-and-drag scrolling (mouse only; touch scrolls natively).
  // Cards stay clickable: a drag only starts once the pointer clearly moves,
  // and the click that follows a drag is swallowed so links don't fire.
  function initDrag(track, onDragEnd) {
    var DRAG_THRESHOLD = 6;
    var isDown = false;
    var dragging = false;
    var suppressClick = false;
    var startX = 0;
    var startScroll = 0;
    var pointerId = null;

    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      isDown = true;
      dragging = false;
      startX = e.clientX;
      startScroll = track.scrollLeft;
      pointerId = e.pointerId;
    });

    track.addEventListener('pointermove', function (e) {
      if (!isDown || e.pointerId !== pointerId) return;
      var dx = e.clientX - startX;
      if (!dragging) {
        if (Math.abs(dx) < DRAG_THRESHOLD) return;
        // Nothing to scroll — leave clicks alone.
        if (track.scrollWidth - track.clientWidth <= 2) return;
        dragging = true;
        track.classList.add(GRABBING_CLASS);
        try { track.setPointerCapture(pointerId); } catch (err) {}
      }
      e.preventDefault();
      track.scrollLeft = startScroll - dx;
    });

    function endDrag(e) {
      if (!isDown || (pointerId !== null && e.pointerId !== pointerId)) return;
      isDown = false;
      if (dragging) {
        track.classList.remove(GRABBING_CLASS);
        try { track.releasePointerCapture(pointerId); } catch (err) {}
        suppressClick = true;
        if (typeof onDragEnd === 'function') onDragEnd();
      }
      dragging = false;
      pointerId = null;
    }

    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);

    track.addEventListener('click', function (e) {
      if (suppressClick) {
        e.preventDefault();
        e.stopPropagation();
        suppressClick = false;
      }
    }, true);

    // Always cancel the browser's native image/link drag so a click-and-hold
    // reliably starts a carousel scroll instead of dragging the image.
    track.addEventListener('dragstart', function (e) {
      e.preventDefault();
    });
  }

  function initSection(section) {
    if (!section || section.dataset.odditInitialized === 'true') return;
    section.dataset.odditInitialized = 'true';

    var track = section.querySelector('.oddit-section-featured-reviews__track');
    var prevBtn = section.querySelector('[data-carousel-prev]');
    var nextBtn = section.querySelector('[data-carousel-next]');
    var indicators = section.querySelector('.oddit-section-featured-reviews__indicators');
    var cards = Array.prototype.slice.call(
      section.querySelectorAll('.oddit-section-featured-reviews__card')
    );

    if (!track || cards.length === 0) return;

    var dots = [];

    // Distance between the start of one card and the next (card width + gap).
    function getStep() {
      if (cards.length < 2) {
        return cards[0] ? cards[0].getBoundingClientRect().width : 0;
      }
      var a = cards[0].getBoundingClientRect();
      var b = cards[1].getBoundingClientRect();
      return b.left - a.left;
    }

    function maxScroll() {
      return track.scrollWidth - track.clientWidth;
    }

    function isScrollable() {
      return maxScroll() > 2;
    }

    function atStart() {
      return track.scrollLeft <= 2;
    }

    function atEnd() {
      return track.scrollLeft >= maxScroll() - 2;
    }

    // Number of distinct scroll stops (pages), based on how far the track can
    // scroll relative to one card step. 1 means everything fits — no paging.
    function getPageCount() {
      var step = getStep();
      if (step <= 0) return 1;
      var max = maxScroll();
      if (max <= 2) return 1;
      return Math.round(max / step) + 1;
    }

    function getActiveIndex() {
      var step = getStep();
      if (step <= 0) return 0;
      var idx = Math.round(track.scrollLeft / step);
      return Math.max(0, Math.min(idx, dots.length - 1));
    }

    function scrollBehavior() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'auto'
        : 'smooth';
    }

    function scrollToIndex(index) {
      var step = getStep();
      if (step <= 0) return;
      var target = step * index;
      var max = maxScroll();
      if (target > max) target = max;
      if (target < 0) target = 0;
      track.scrollTo({ left: target, behavior: scrollBehavior() });
    }

    // (Re)build the dots so their count always matches the current page count.
    function buildDots() {
      if (!indicators) return;
      var count = getPageCount();
      if (dots.length === count) return;

      indicators.innerHTML = '';
      dots = [];

      for (var i = 0; i < count; i++) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = DOT_CLASS;
        dot.setAttribute('aria-label', 'Go to review page ' + (i + 1));
        dot.setAttribute('data-carousel-dot', String(i));

        var inner = document.createElement('span');
        inner.setAttribute('aria-hidden', 'true');
        inner.innerHTML = '&zwnj;';
        dot.appendChild(inner);

        (function (index) {
          dot.addEventListener('click', function () {
            scrollToIndex(index);
          });
          dot.addEventListener('keydown', function (e) {
            if (e.key === 'ArrowRight') {
              e.preventDefault();
              var n = (index + 1) % dots.length;
              dots[n].focus();
              scrollToIndex(n);
            } else if (e.key === 'ArrowLeft') {
              e.preventDefault();
              var p = (index - 1 + dots.length) % dots.length;
              dots[p].focus();
              scrollToIndex(p);
            }
          });
        })(i);

        indicators.appendChild(dot);
        dots.push(dot);
      }
    }

    function updateControls() {
      var scrollable = isScrollable();

      // Arrows only show when there is somewhere to scroll, and each hides
      // once it reaches its respective end.
      if (prevBtn) prevBtn.hidden = !scrollable || atStart();
      if (nextBtn) nextBtn.hidden = !scrollable || atEnd();

      // Dots are pointless when everything fits in view or there is only a
      // single page.
      if (indicators) indicators.hidden = !scrollable || dots.length <= 1;

      var active = getActiveIndex();
      dots.forEach(function (dot, i) {
        if (i === active) {
          dot.classList.add(DOT_ACTIVE_CLASS);
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.classList.remove(DOT_ACTIVE_CLASS);
          dot.removeAttribute('aria-current');
        }
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        track.scrollBy({ left: getStep(), behavior: scrollBehavior() });
      });
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        track.scrollBy({ left: -getStep(), behavior: scrollBehavior() });
      });
    }

    var scrollRaf = null;
    track.addEventListener(
      'scroll',
      function () {
        if (scrollRaf) cancelAnimationFrame(scrollRaf);
        scrollRaf = requestAnimationFrame(updateControls);
      },
      { passive: true }
    );

    function recalc() {
      buildDots();
      updateControls();
    }

    if ('ResizeObserver' in window) {
      new ResizeObserver(recalc).observe(track);
    } else {
      window.addEventListener('resize', recalc);
    }

    // After a mouse drag ends, glide smoothly to the nearest card instead of
    // letting scroll-snap re-engage with an instant jump. Snap is held off
    // during the glide so the smooth scroll isn't interrupted, then restored.
    function snapToNearest() {
      var max = maxScroll();
      if (max <= 2) return;
      var step = getStep();
      if (step <= 0) return;

      var target = Math.round(track.scrollLeft / step) * step;
      target = Math.max(0, Math.min(target, max));

      track.style.scrollSnapType = 'none';
      var restore = function () { track.style.scrollSnapType = ''; };
      var fallback = setTimeout(restore, 700);
      track.addEventListener('scrollend', function onEnd() {
        clearTimeout(fallback);
        restore();
        track.removeEventListener('scrollend', onEnd);
      }, { once: true });

      track.scrollTo({ left: target, behavior: scrollBehavior() });
    }

    initDrag(track, snapToNearest);
    recalc();
  }

  function init() {
    document.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target.querySelector
      ? e.target.querySelector(SECTION_SELECTOR)
      : null;
    if (section) initSection(section);
  });
})();
